import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";
import { authGet, setUnauthorizedHandler } from "@/api/client";
import { clearSession, getToken, onSessionChange, setToken } from "./session";

/* Auth runtime.
 *
 * SESSION = TOKEN, TIER = SERVER. The signed-in identity is whatever
 * GET /my/profile returns; nothing is read from storage to decide who
 * someone is or what they may do. On 401 the session is torn down here, so
 * every screen reacts at once instead of each one discovering it separately.
 */

export type Profile = {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  role: string | null;
  plan: string | null;
  /** The server's computed field -- drives Renew vs Subscribe verbatim. */
  subscription_state: string | null;
  subscription_status: string | null;
  email_verified: boolean | null;
  /** Set when the member asked for deletion. The account is deactivated and
   *  every private route but this one is closed; signing in cancels it. */
  deletion_requested_at?: string | null;
  [k: string]: unknown;
};

type AuthValue = {
  /** null = signed out, undefined = still resolving on first load. */
  profile: Profile | null | undefined;
  signedIn: boolean;
  signIn: (token: string) => Promise<Profile | null>;
  signOut: () => void;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({
  children, onSignedOut,
}: { children: ReactNode; onSignedOut?: () => void }) {
  const [profile, setProfile] = useState<Profile | null | undefined>(
    getToken() ? undefined : null,
  );

  const load = useCallback(async (): Promise<Profile | null> => {
    if (!getToken()) { setProfile(null); return null; }
    try {
      const rows = await authGet<Profile>("my/profile");
      const p = rows[0] ?? null;
      setProfile(p);
      return p;
    } catch {
      // A 401 already cleared the token via the transport; anything else
      // leaves the session alone and simply has no profile to show.
      setProfile(getToken() ? null : null);
      return null;
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // One place turns a 401 into a signed-out app.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setProfile(null);
      onSignedOut?.();
    });
    return () => setUnauthorizedHandler(null);
  }, [onSignedOut]);

  useEffect(() => onSessionChange((tok) => { if (!tok) setProfile(null); }), []);

  const signIn = useCallback(async (token: string) => {
    setToken(token);
    return load();
  }, [load]);

  const signOut = useCallback(() => {
    clearSession();
    setProfile(null);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      profile,
      signedIn: !!profile,
      signIn,
      signOut,
      refresh: async () => { await load(); },
    }),
    [profile, signIn, signOut, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
